import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, Save, Plus, Trash2, GripVertical, ChevronsDown, Truck, Copy, Percent, Undo2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("mv_admin_token")}` } });

// Common transport line-item templates. Selecting one from the "Aggiungi voce comune"
// dropdown pre-fills a new row in the currently-focused section.
const TRANSPORT_TEMPLATES = [
  { group: "Spedizioni", items: [
    { description: "Consegna Nazionale", price: 0, unit: "€ / spedizione" },
    { description: "Consegna Regionale", price: 0, unit: "€ / spedizione" },
    { description: "Consegna Provinciale", price: 0, unit: "€ / spedizione" },
    { description: "Ritiro", price: 0, unit: "€ / spedizione" },
    { description: "Ritiro urgente / express", price: 0, unit: "€ / spedizione" },
    { description: "Sabato / festivo", price: 0, unit: "€ / spedizione" },
    { description: "Consegna fuori orario", price: 0, unit: "€ / spedizione" },
    { description: "Contrassegno (COD)", price: 0, unit: "€ / spedizione" },
    { description: "Assicurazione supplementare", price: 0, unit: "€ / spedizione" },
    { description: "Consegna ADR (merci pericolose)", price: 0, unit: "€ / spedizione" },
    { description: "Consegna refrigerata", price: 0, unit: "€ / spedizione" },
    { description: "Consegna al piano (senza ascensore)", price: 0, unit: "€ / spedizione" },
  ]},
  { group: "Servizi Extra Spedizione", items: [
    { description: "Sponda idraulica", price: 0, unit: "€ / spedizione" },
    { description: "Facchinaggio", price: 0, unit: "€ / ora" },
    { description: "Ripristino imballo", price: 0, unit: "€ / forfait" },
    { description: "Ritiro tassativo (finestra oraria)", price: 0, unit: "€ / spedizione" },
    { description: "Fermo camion / attesa", price: 0, unit: "€ / ora" },
    { description: "Furgone completo", price: 0, unit: "€ / forfait" },
    { description: "Camion completo", price: 0, unit: "€ / metro" },
    { description: "Sosta prolungata", price: 0, unit: "€ / ora" },
  ]},
  { group: "Magazzino", items: [
    { description: "Stoccaggio bancali", price: 0, unit: "€ / bancale" },
    { description: "Handling ingresso merce", price: 0, unit: "€ / bancale" },
    { description: "Handling uscita merce", price: 0, unit: "€ / bancale" },
    { description: "Deposito temporaneo", price: 0, unit: "€ / bancale" },
    { description: "Assicurazione All Risk magazzino", price: 0, unit: "€ / mese" },
    { description: "Manodopera preparazione spedizione", price: 0, unit: "€ / ora" },
    { description: "Manodopera carico", price: 0, unit: "€ / ora" },
    { description: "Manodopera scarico", price: 0, unit: "€ / ora" },
    { description: "Inventario / controllo qualità", price: 0, unit: "€ / ora" },
    { description: "Etichettatura colli", price: 0, unit: "€ / bancale" },
  ]},
  { group: "Amministrativo", items: [
    { description: "Emissione DDT extra", price: 0, unit: "€ (una tantum)" },
    { description: "Ricerca documenti / POD", price: 0, unit: "€ (una tantum)" },
    { description: "Fermo deposito", price: 0, unit: "€ / mese" },
  ]},
];

const DEFAULT_SECTIONS = [
  { title: "Servizi Spedizione", lines: [] },
  { title: "Magazzino e Manodopera", lines: [] },
  { title: "Extra e Amministrativo", lines: [] },
];

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function CustomPriceListModal({ email, clientName, onClose, onSaved }) {
  const [sections, setSections] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [showClone, setShowClone] = useState(false);
  const [cloneSources, setCloneSources] = useState([]);
  const [cloning, setCloning] = useState(false);
  const [showBulkPct, setShowBulkPct] = useState(false);
  const [bulkPct, setBulkPct] = useState("");
  const [bulkSkipZero, setBulkSkipZero] = useState(true);
  const [lastBulkUndo, setLastBulkUndo] = useState(null); // { snapshot: sections[], pct: number, count: number }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/clients/${encodeURIComponent(email)}/custom-price-list`, authHeaders());
      setUnits(r.data.units || []);
      let secs = r.data.sections || [];
      if (secs.length === 0) {
        // Provide default empty sections so the user has a starting point
        secs = DEFAULT_SECTIONS.map((s, i) => ({ id: genId(), title: s.title, order: i, lines: [] }));
      }
      setSections(secs);
      setActiveSectionId(secs[0]?.id || null);
    } catch {
      toast.error("Errore caricamento listino");
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => { load(); }, [load]);

  const addSection = () => {
    const s = { id: genId(), title: `Sezione ${sections.length + 1}`, order: sections.length, lines: [] };
    setSections([...sections, s]);
    setActiveSectionId(s.id);
  };

  const removeSection = (sid) => {
    if (!window.confirm("Eliminare questa sezione e tutte le sue voci?")) return;
    setSections(sections.filter((s) => s.id !== sid));
  };

  const updateSection = (sid, patch) => {
    setSections(sections.map((s) => s.id === sid ? { ...s, ...patch } : s));
  };

  const addLine = (sid, preset) => {
    const line = {
      id: genId(),
      description: preset?.description || "",
      price: preset?.price ?? 0,
      unit: preset?.unit || units[0] || "€ / forfait",
    };
    setSections(sections.map((s) => s.id === sid ? { ...s, lines: [...s.lines, line] } : s));
  };

  const updateLine = (sid, lid, patch) => {
    setSections(sections.map((s) => s.id === sid
      ? { ...s, lines: s.lines.map((l) => l.id === lid ? { ...l, ...patch } : l) }
      : s
    ));
  };

  const removeLine = (sid, lid) => {
    setSections(sections.map((s) => s.id === sid
      ? { ...s, lines: s.lines.filter((l) => l.id !== lid) }
      : s
    ));
  };

  const addTemplate = (tpl) => {
    const targetId = activeSectionId || sections[0]?.id;
    if (!targetId) { toast.error("Aggiungi prima una sezione"); return; }
    addLine(targetId, tpl);
    toast.success(`Aggiunta: ${tpl.description}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        sections: sections.map((s, i) => ({
          id: s.id, title: s.title, order: i,
          lines: s.lines.map((l) => ({
            id: l.id, description: l.description, price: Number(l.price) || 0, unit: l.unit,
          })),
        })),
      };
      const r = await axios.put(`${API}/admin/clients/${encodeURIComponent(email)}/custom-price-list`, payload, authHeaders());
      toast.success(r.data.line_count > 0 ? `Listino salvato — ${r.data.line_count} voci` : "Listino svuotato — torna al layout automatico");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const openClonePicker = async () => {
    setShowClone(true);
    try {
      const r = await axios.get(`${API}/admin/clients-with-custom-price-list`, authHeaders());
      setCloneSources((r.data || []).filter((c) => c.email !== email));
    } catch {
      toast.error("Errore caricamento clienti");
    }
  };

  const applyBulkPct = () => {
    const raw = (bulkPct || "").toString().replace(",", ".").replace("%", "").trim();
    const pct = parseFloat(raw);
    if (isNaN(pct) || pct === 0) {
      toast.error("Inserisci una percentuale valida (es. 5 o -3.5)");
      return;
    }
    const factor = 1 + pct / 100;
    let updated = 0;
    // Deep-clone the sections we're about to change so Undo can restore the exact prices
    const snapshot = sections.map((s) => ({
      ...s,
      lines: s.lines.map((l) => ({ ...l })),
    }));
    const next = sections.map((s) => ({
      ...s,
      lines: s.lines.map((l) => {
        const cur = Number(l.price) || 0;
        if (bulkSkipZero && cur === 0) return l;
        const np = Math.max(0, Math.round(cur * factor * 100) / 100);
        if (np !== cur) updated += 1;
        return { ...l, price: np };
      }),
    }));
    setSections(next);
    setShowBulkPct(false);
    setBulkPct("");
    if (updated > 0) {
      setLastBulkUndo({ snapshot, pct, count: updated });
      toast.success(
        `Applicato ${pct > 0 ? "+" : ""}${pct}% a ${updated} voci — usa "Annulla" per ripristinare`
      );
    } else {
      toast.success("Nessuna voce modificata");
    }
  };

  const undoBulkPct = () => {
    if (!lastBulkUndo) return;
    setSections(lastBulkUndo.snapshot);
    const { pct, count } = lastBulkUndo;
    setLastBulkUndo(null);
    toast.success(`Ripristinati ${count} prezzi (annullato ${pct > 0 ? "+" : ""}${pct}%)`);
  };

  const cloneFrom = async (srcEmail) => {
    const hasContent = sections.some((s) => (s.lines || []).length > 0);
    if (hasContent && !window.confirm(
      "Il listino attuale verrà sostituito dalle voci del cliente selezionato. Continuare?"
    )) return;
    setCloning(true);
    try {
      const r = await axios.post(
        `${API}/admin/clients/${encodeURIComponent(email)}/custom-price-list/clone-from`,
        { source_email: srcEmail }, authHeaders()
      );
      // Refresh local state with cloned data
      setSections(r.data.sections || []);
      setActiveSectionId(r.data.sections?.[0]?.id || null);
      toast.success(`Listino clonato — ${r.data.line_count} voci copiate`);
      setShowClone(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore clonazione");
    } finally {
      setCloning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="custom-pl-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#09090B]">Listino Personalizzato</h3>
            <p className="text-xs text-zinc-500">{clientName} · {email}</p>
          </div>
          <div className="flex items-center gap-2">
            {lastBulkUndo && (
              <button
                onClick={undoBulkPct}
                className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 text-amber-800 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-amber-100"
                data-testid="custom-pl-bulk-undo-btn"
                title={`Annulla ${lastBulkUndo.pct > 0 ? "+" : ""}${lastBulkUndo.pct}% su ${lastBulkUndo.count} voci`}
              >
                <Undo2 size={12} /> Annulla ritocco ({lastBulkUndo.pct > 0 ? "+" : ""}{lastBulkUndo.pct}%)
              </button>
            )}
            <div className="relative">
              <button
                onClick={() => setShowBulkPct((v) => !v)}
                className="inline-flex items-center gap-1 border border-zinc-300 bg-white text-zinc-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-50"
                data-testid="custom-pl-bulk-pct-btn"
                title="Applica un aumento o sconto percentuale a tutte le voci"
              >
                <Percent size={12} /> ±%
              </button>
              {showBulkPct && (
                <div
                  className="absolute right-0 top-full mt-1 z-10 w-72 bg-white border border-zinc-200 rounded-sm shadow-lg p-3"
                  data-testid="custom-pl-bulk-pct-popover"
                >
                  <p className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 mb-2">
                    Ritocco prezzi
                  </p>
                  <label className="block text-xs text-zinc-700 mb-1">
                    Percentuale (es. <b>5</b> = +5%, <b>-3.5</b> = sconto)
                  </label>
                  <div className="flex items-center gap-1 mb-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={bulkPct}
                      onChange={(e) => setBulkPct(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") applyBulkPct(); }}
                      placeholder="es. 5"
                      autoFocus
                      className="flex-1 h-8 px-2 border border-zinc-300 rounded-sm text-sm outline-none focus:border-blue-500"
                      data-testid="custom-pl-bulk-pct-input"
                    />
                    <span className="text-sm text-zinc-500">%</span>
                  </div>
                  <label className="flex items-center gap-2 text-[11px] text-zinc-600 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkSkipZero}
                      onChange={(e) => setBulkSkipZero(e.target.checked)}
                      data-testid="custom-pl-bulk-pct-skipzero"
                    />
                    Salta le voci con prezzo 0 €
                  </label>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => { setShowBulkPct(false); setBulkPct(""); }}
                      className="text-xs px-3 py-1.5 border border-zinc-300 rounded-sm text-zinc-700 hover:bg-zinc-50"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={applyBulkPct}
                      className="text-xs px-3 py-1.5 bg-[#FF5200] text-white font-bold uppercase rounded-sm hover:bg-orange-600"
                      data-testid="custom-pl-bulk-pct-apply"
                    >
                      Applica
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={openClonePicker}
              className="inline-flex items-center gap-1 border border-zinc-300 bg-white text-zinc-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-50"
              data-testid="custom-pl-clone-btn"
              title="Copia le voci del listino da un altro cliente"
            >
              <Copy size={12} /> Clona da…
            </button>
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="custom-pl-close"><X size={20} /></button>
          </div>
        </div>

        <div className="p-5 flex-1 overflow-auto space-y-4">
          {loading ? (
            <div className="text-center py-10 text-zinc-500"><Loader2 size={20} className="animate-spin inline mr-2" /> Caricamento…</div>
          ) : (
            <>
              {/* Templates dropdown */}
              <details className="bg-zinc-50 border border-zinc-200 rounded-sm">
                <summary className="cursor-pointer px-4 py-2 text-sm font-bold text-[#09090B] flex items-center gap-2">
                  <Truck size={14} className="text-[#FF5200]" /> Aggiungi voce comune (trasporto, magazzino, extra)
                  <ChevronsDown size={12} className="ml-auto text-zinc-400" />
                </summary>
                <div className="p-3 space-y-3 border-t border-zinc-200 max-h-64 overflow-auto">
                  {TRANSPORT_TEMPLATES.map((grp) => (
                    <div key={grp.group}>
                      <p className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 mb-1">{grp.group}</p>
                      <div className="flex flex-wrap gap-1">
                        {grp.items.map((it) => (
                          <button
                            key={it.description}
                            onClick={() => addTemplate(it)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-zinc-300 bg-white rounded-sm hover:bg-orange-50 hover:border-[#FF5200]"
                            data-testid={`tpl-${it.description.replace(/\W/g,'-').toLowerCase()}`}
                            title={`Unità: ${it.unit}`}
                          >
                            <Plus size={10} /> {it.description}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </details>

              {sections.map((sec) => (
                <div key={sec.id} className={`border rounded-sm ${activeSectionId === sec.id ? "border-[#FF5200] bg-orange-50/30" : "border-zinc-200 bg-white"}`}
                     onClick={() => setActiveSectionId(sec.id)}
                     data-testid={`section-${sec.id}`}>
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100">
                    <GripVertical size={14} className="text-zinc-300" />
                    <input
                      type="text"
                      value={sec.title}
                      onChange={(e) => updateSection(sec.id, { title: e.target.value })}
                      className="flex-1 h-8 px-2 text-sm font-bold text-[#09090B] bg-transparent outline-none focus:bg-white focus:border focus:border-blue-500 focus:rounded-sm"
                      data-testid={`section-title-${sec.id}`}
                    />
                    <button onClick={() => addLine(sec.id)} className="inline-flex items-center gap-1 border border-zinc-300 bg-white text-[11px] font-bold uppercase px-2 py-1 rounded-sm hover:bg-zinc-50" data-testid={`add-line-${sec.id}`}>
                      <Plus size={11} /> Voce
                    </button>
                    <button onClick={() => removeSection(sec.id)} className="text-red-400 hover:text-red-600 p-1" title="Elimina sezione">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {sec.lines.length === 0 ? (
                    <p className="px-4 py-4 text-xs text-zinc-400 italic">Nessuna voce. Aggiungi una voce comune sopra o usa "+ Voce".</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50 border-b border-zinc-100">
                        <tr>
                          <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Descrizione</th>
                          <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 w-32">Prezzo (€)</th>
                          <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 w-48">Unità di Misura</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sec.lines.map((line) => (
                          <tr key={line.id} className="border-t border-zinc-50" data-testid={`line-${line.id}`}>
                            <td className="px-3 py-1.5">
                              <input
                                type="text"
                                value={line.description}
                                onChange={(e) => updateLine(sec.id, line.id, { description: e.target.value })}
                                placeholder="Es. Consegna nazionale via corriere"
                                className="w-full h-8 px-2 border border-zinc-200 rounded-sm text-sm focus:border-blue-500 outline-none"
                                data-testid={`line-desc-${line.id}`}
                              />
                            </td>
                            <td className="px-3 py-1.5">
                              <input
                                type="number" step="0.01" min="0"
                                value={line.price}
                                onChange={(e) => updateLine(sec.id, line.id, { price: parseFloat(e.target.value) || 0 })}
                                className="w-full h-8 px-2 border border-zinc-200 rounded-sm text-sm text-right focus:border-blue-500 outline-none"
                                data-testid={`line-price-${line.id}`}
                              />
                            </td>
                            <td className="px-3 py-1.5">
                              <select
                                value={line.unit}
                                onChange={(e) => updateLine(sec.id, line.id, { unit: e.target.value })}
                                className="w-full h-8 px-2 border border-zinc-200 rounded-sm text-sm bg-white focus:border-blue-500 outline-none"
                                data-testid={`line-unit-${line.id}`}
                              >
                                {units.map((u) => <option key={u} value={u}>{u}</option>)}
                              </select>
                            </td>
                            <td className="px-2">
                              <button onClick={() => removeLine(sec.id, line.id)} className="text-red-400 hover:text-red-600 p-1" title="Elimina voce">
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}

              <button
                onClick={addSection}
                className="w-full border-2 border-dashed border-zinc-300 rounded-sm py-3 text-sm text-zinc-500 hover:border-[#FF5200] hover:text-[#FF5200] hover:bg-orange-50 font-bold uppercase tracking-wide"
                data-testid="add-section-btn"
              >
                <Plus size={14} className="inline mr-1" /> Aggiungi Sezione
              </button>

              <p className="text-[11px] text-zinc-500 leading-relaxed">
                💡 <b>Come funziona</b>: le voci qui inserite <b>sostituiscono</b> il layout automatico del listino PDF (stoccaggio/manodopera/extra) per questo cliente.
                Se svuoti tutte le sezioni e salvi, il PDF torna al layout automatico basato sul Contratto Stoccaggio.
              </p>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2 bg-zinc-50">
          <button onClick={onClose} className="border border-zinc-300 bg-white text-zinc-700 px-4 py-2 text-xs font-bold uppercase rounded-sm">Annulla</button>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 py-2 text-xs font-bold uppercase rounded-sm hover:bg-blue-800 disabled:opacity-50"
            data-testid="custom-pl-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Salva Listino
          </button>
        </div>
      </div>

      {showClone && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="clone-picker-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowClone(false); }}
        >
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <h4 className="font-bold text-[#09090B]">Clona Listino da un Cliente</h4>
                <p className="text-[11px] text-zinc-500">Seleziona il cliente da cui copiare le voci</p>
              </div>
              <button onClick={() => setShowClone(false)} className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
            </div>
            <div className="p-2 overflow-auto">
              {cloneSources.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-8">Nessun altro cliente ha ancora un listino personalizzato da clonare.</p>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {cloneSources.map((c) => (
                    <li key={c.email}>
                      <button
                        onClick={() => cloneFrom(c.email)}
                        disabled={cloning}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-orange-50 text-left disabled:opacity-50"
                        data-testid={`clone-source-${c.email}`}
                      >
                        <Copy size={14} className="text-zinc-400" />
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm text-[#09090B] truncate">{c.name}</div>
                          <div className="text-[10px] text-zinc-500">{c.email}</div>
                        </div>
                        <span className="text-[10px] uppercase font-bold tracking-widest text-zinc-500">
                          {c.sections_count} sez · {c.line_count} voci
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {cloning && <div className="text-center text-xs text-zinc-500 py-2"><Loader2 size={12} className="inline animate-spin mr-1"/> Clonazione in corso…</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
